import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import planejamentoHorasService from '../../services/planejamentoHoras.service';
import lancamentoHorasService from '../../services/lancamentoHoras.service';
import clienteService from '../../services/cliente.service';
import CalendarioMensal from './CalendarioMensal';
import SePode from '../../components/SePode';   // r90
// r72 item 7: LancamentoModal não é mais usado (edição removida)

// ════════════════════════════════════════════════════════════════════════════
// PlanejamentoModal — Rotina de Planejamento de Alocação (r71)
// ────────────────────────────────────────────────────────────────────────────
// Refatorado: form em 3 linhas, combos com seta nativa, cor por projeto,
// navegação do calendário no próprio header, deslocamento, botão Salvar ícone.
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',  cinzaSt: '#f3f4f6',
  verde:   '#10b981', verdeBg: '#f0fff4',
  vermelho:'#dc2626', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  laranja: '#f97316',
};

const TIPO_LANC_LABEL = {
  P: 'Projeto',
  A: 'Atendimento Pontual',
  D: 'Service Desk',
  S: 'Sustentação',
};
const MAP_TIPO_NEG_PRA_LANC = { P: 'P', U: 'A', S: 'D', T: 'S' };

const DIAS_SEMANA_OPCOES = [
  { v: 0, label: 'Domingo'  },
  { v: 1, label: 'Segunda'  },
  { v: 2, label: 'Terça'    },
  { v: 3, label: 'Quarta'   },
  { v: 4, label: 'Quinta'   },
  { v: 5, label: 'Sexta'    },
  { v: 6, label: 'Sábado'   },
];

const labelStyle = { fontSize: '11px', color: COR.muted, marginBottom: '3px', display: 'block', fontWeight: '500' };
const inputStyle = {
  width: '100%', padding: '6px 10px', fontSize: '13px',
  border: `1px solid ${COR.borda}`, borderRadius: '4px',
  fontFamily: "'Manrope', sans-serif",
  color: COR.texto, outline: 'none', boxSizing: 'border-box', height: '32px',
};

// r74: paleta de 12 cores em matizes uniformemente espaçados (HSL hue a cada 30°),
//      mesma saturação e luminosidade — todas distintas entre si.
//      Ordem original (matiz crescente):
//        0: vermelho, 1: laranja, 2: amarelo, 3: verde-amarelado, 4: verde,
//        5: verde-azulado, 6: ciano, 7: azul-céu, 8: azul, 9: violeta,
//        10: magenta, 11: rosa
const PALETA_DEFAULT = [
  '#e06c6c', '#e0a66c', '#e0e06c', '#a6e06c', '#6ce06c', '#6ce0a6',
  '#6ce0e0', '#6ca6e0', '#6c6ce0', '#a66ce0', '#e06ce0', '#e06ca6',
];

// r74: ordem de prioridade pra escolher cores quando há poucos projetos visíveis.
//      Os primeiros índices são os MAIS DISTANTES entre si no círculo de matizes
//      (pega lados opostos antes de pegar vizinhos).
//      Ex: 1 projeto → vermelho; 2 → vermelho + ciano (opostos);
//          4 projetos → vermelho, ciano, verde-amarelado, violeta (90° entre si).
const ORDEM_PRIORIDADE = [0, 6, 3, 9, 1, 7, 4, 10, 2, 8, 5, 11];

// r74: aloca cores pra uma lista de projetos de forma inteligente:
//      - 1º projeto pega cor de prioridade 0 (vermelho)
//      - 2º pega prioridade 1 (cor mais distante do 1º) — etc
//      - Ordena projetos por ID pra garantir estabilidade entre renderizações
//      Retorna um Map<projeto_id, cor>.
function alocarCoresProjetos(projetoIds) {
  const ordenados = [...new Set((projetoIds || []).filter(Boolean))].sort();
  const mapa = new Map();
  ordenados.forEach((pid, idx) => {
    const corIdx = ORDEM_PRIORIDADE[idx % ORDEM_PRIORIDADE.length];
    mapa.set(pid, PALETA_DEFAULT[corIdx]);
  });
  return mapa;
}

// r74: fallback solo (sem contexto) — usado quando precisamos da cor de UM
//      projeto isolado e não temos a lista visível. Mantém comportamento
//      determinístico antigo (hash do ID). Não tenta evitar colisão.
function corDoProjeto(projetoId) {
  if (!projetoId) return '#cbd5e1';
  let h = 0;
  for (let i = 0; i < projetoId.length; i++) h = ((h << 5) - h) + projetoId.charCodeAt(i);
  return PALETA_DEFAULT[Math.abs(h) % PALETA_DEFAULT.length];
}

function hhmmToDec(s) {
  if (!s) return 0;
  const [h, m] = String(s).split(':').map(Number);
  return (h || 0) + ((m || 0) / 60);
}

function ddmmaaaa(s) {
  if (!s) return '';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}

// Tag colorida do cliente (mesmo padrão das demais telas)
function TagCliente({ tag, cor }) {
  if (!tag) return null;
  const bgCor = cor || '#003399';
  const hex = bgCor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) || 0;
  const g = parseInt(hex.substr(2, 2), 16) || 0;
  const b = parseInt(hex.substr(4, 2), 16) || 0;
  const corTexto = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#1a1a2e' : '#ffffff';
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: '20px',
      fontSize: '10px', fontWeight: '500', backgroundColor: bgCor, color: corTexto,
      letterSpacing: '0.3px', flexShrink: 0,
    }}>{tag}</span>
  );
}

// ─── <Combobox> refeito com seta tipo select e renderItem custom (r71) ──────
// Props: label, placeholder, buscar(q), onSelecionar, disabled, renderItem?
function Combobox({ label, placeholder, buscar, onSelecionar, disabled, renderItem }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [opcoes, setOpcoes] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!aberto) return;
    let cancelado = false;
    const tid = setTimeout(async () => {
      try {
        const lista = await buscar(busca);
        if (!cancelado) setOpcoes(lista);
      } catch (e) { console.warn(e); }
    }, 200);
    return () => { cancelado = true; clearTimeout(tid); };
  }, [aberto, busca, buscar]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => !disabled && setAberto(a => !a)}
        disabled={disabled}
        style={{
          ...inputStyle,
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: disabled ? COR.cinzaBg : COR.branco,
          textAlign: 'left',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          paddingRight: '26px',  // r71: espaço pra setinha
          position: 'relative',
        }}>
        <span style={{
          color: label ? COR.texto : COR.muted,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {label || placeholder}
        </span>
        {/* r71: setinha de combo tipo select nativo */}
        <span style={{
          position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
          color: COR.muted, pointerEvents: 'none',
        }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2.5 4l2.5 2.5L7.5 4" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>
      {aberto && !disabled && (
        <div style={{
          position: 'absolute', top: '34px', left: 0, right: 0,
          maxHeight: '260px', overflow: 'hidden',
          backgroundColor: COR.branco, border: `1px solid ${COR.borda}`,
          borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          zIndex: 50, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '6px 8px', borderBottom: `1px solid ${COR.borda}`, backgroundColor: COR.cinzaBg }}>
            <input type="text" value={busca} onChange={e => setBusca(e.target.value)} autoFocus
              placeholder="Buscar..."
              style={{ width: '100%', height: '24px', padding: '0 6px',
                       border: `1px solid ${COR.borda}`, borderRadius: '3px',
                       fontSize: '12px', fontFamily: "'Manrope', sans-serif", outline: 'none' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {opcoes.length === 0 ? (
              <div style={{ padding: '10px', fontSize: '12px', color: COR.muted, fontStyle: 'italic' }}>
                Nada encontrado.
              </div>
            ) : opcoes.map(op => (
              <div key={op.value}
                onClick={() => { onSelecionar(op); setAberto(false); setBusca(''); }}
                style={{
                  padding: '7px 10px', fontSize: '13px', cursor: 'pointer',
                  borderBottom: `1px solid ${COR.borda}`,
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = COR.azulBg}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = COR.branco}>
                {renderItem ? renderItem(op) : op.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── <ModalConflitos> ───────────────────────────────────────────────────────
// r71: agora exibe projeto descrição e cliente fantasia
function ModalConflitos({ conflitos, onConfirmar, onCancelar }) {
  if (!conflitos || conflitos.length === 0) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onCancelar}>
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: COR.branco, borderRadius: '8px', width: '720px', maxWidth: '90%',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        fontFamily: "'Manrope', sans-serif",
      }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${COR.borda}`,
                       fontSize: '15px', fontWeight: '700', color: COR.amarelo }}>
          ⚠ Conflitos detectados ({conflitos.length})
        </div>
        <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1, fontSize: '13px' }}>
          <p style={{ marginTop: 0, color: COR.texto }}>
            Os blocos abaixo conflitam com lançamentos existentes. Se confirmar,
            os blocos novos serão criados <strong>sobrepondo</strong> os existentes (não serão excluídos).
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginTop: '8px' }}>
            <thead>
              <tr style={{ backgroundColor: COR.cinzaBg, borderBottom: `1px solid ${COR.borda}` }}>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '700' }}>Data</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '700' }}>Novo bloco</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '700' }}>Conflita com</th>
              </tr>
            </thead>
            <tbody>
              {conflitos.map((c, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${COR.borda}` }}>
                  <td style={{ padding: '6px 8px', verticalAlign: 'top' }}>{ddmmaaaa(c.data)}</td>
                  <td style={{ padding: '6px 8px', verticalAlign: 'top' }}>
                    {c.novo.hora_inicio}–{c.novo.hora_fim}
                  </td>
                  <td style={{ padding: '6px 8px', color: COR.muted, verticalAlign: 'top' }}>
                    <div>{c.existe.hora_inicio}–{c.existe.hora_fim}</div>
                    {/* r71: detalhes do conflito */}
                    <div style={{ fontSize: '11px', color: COR.texto, marginTop: '2px' }}>
                      {c.existe.projeto_id}
                      {c.existe.projeto_descricao ? ` — ${c.existe.projeto_descricao}` : ''}
                    </div>
                    {c.existe.cliente_fantasia && (
                      <div style={{ fontSize: '10px', color: COR.muted, marginTop: '1px' }}>
                        {c.existe.cliente_fantasia}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
                       display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onCancelar}
            style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                     backgroundColor: COR.branco, color: COR.muted,
                     border: `1px solid ${COR.borda}`, borderRadius: '4px', cursor: 'pointer' }}>
            Cancelar
          </button>
          <button onClick={onConfirmar}
            style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                     backgroundColor: COR.amarelo, color: COR.branco,
                     border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Salvar mesmo assim
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── <ModalFeriados> ────────────────────────────────────────────────────────
function ModalFeriados({ feriados, valores, onChange, onConfirmar, onCancelar }) {
  if (!feriados || feriados.length === 0) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{
        backgroundColor: COR.branco, borderRadius: '8px', width: '500px', maxWidth: '90%',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        fontFamily: "'Manrope', sans-serif",
      }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${COR.borda}`,
                       fontSize: '15px', fontWeight: '700', color: COR.azul }}>
          📅 Feriados encontrados ({feriados.length})
        </div>
        <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1, fontSize: '13px' }}>
          <p style={{ marginTop: 0, color: COR.texto }}>
            Os dias selecionados caem em feriado. Pra cada um, escolha o que fazer:
          </p>
          {feriados.map((f, i) => {
            // r72 item 10: mostrar dia da semana do feriado
            const DIAS_NOMES = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
            const diaSemanaTxt = (typeof f.dow === 'number') ? DIAS_NOMES[f.dow] : '';
            return (
              <div key={f.data} style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 0', borderBottom: `1px solid ${COR.borda}`,
              }}>
                <span style={{ flex: 1, fontWeight: '600' }}>
                  {ddmmaaaa(f.data)}
                  {diaSemanaTxt && (
                    <span style={{ fontWeight: '400', color: COR.muted, marginLeft: '6px', fontSize: '12px' }}>
                      ({diaSemanaTxt})
                    </span>
                  )}
                </span>
                {['PULAR', 'ANTERIOR', 'POSTERIOR'].map(acao => (
                  <label key={acao} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '12px' }}>
                    <input
                      type="radio"
                      name={`fer-${i}`}
                      checked={(valores[f.data] || 'PULAR') === acao}
                      onChange={() => onChange(f.data, acao)}
                    />
                    {acao === 'PULAR' ? 'Pular' : acao === 'ANTERIOR' ? 'Dia anterior' : 'Dia posterior'}
                  </label>
                ))}
              </div>
            );
          })}
        </div>
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
                       display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onCancelar}
            style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                     backgroundColor: COR.branco, color: COR.muted,
                     border: `1px solid ${COR.borda}`, borderRadius: '4px', cursor: 'pointer' }}>
            Cancelar
          </button>
          <button onClick={onConfirmar}
            style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                     backgroundColor: COR.azul, color: COR.branco,
                     border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── <ModalConfirmaExclusao> r75 ────────────────────────────────────────────
// Mostra resumo numérico das horas que serão EXCLUÍDAS (P + C) e
// PRESERVADAS (X + F) ao excluir o grupo de um projeto.
function ModalConfirmaExclusao({ grupo, excluiveis, preservados, onConfirmar, onCancelar }) {
  if (!grupo) return null;
  const formatDataHora = (l) => `${ddmmaaaa(l.data)} ${(l.hora_inicio || '').slice(0, 5)}`;
  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onCancelar}>
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: COR.branco, borderRadius: '8px',
        width: '620px', maxWidth: '90%', maxHeight: '80vh',
        display: 'flex', flexDirection: 'column',
        fontFamily: "'Manrope', sans-serif",
      }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${COR.borda}`,
                       fontSize: '15px', fontWeight: '700', color: COR.vermelho }}>
          🗑 Excluir lançamentos — {grupo.projeto_id}
          {grupo.projeto_descricao && (
            <span style={{ fontWeight: '400', color: COR.muted, fontSize: '12px', marginLeft: '6px' }}>
              {grupo.projeto_descricao}
            </span>
          )}
        </div>
        <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1, fontSize: '13px' }}>
          {/* Excluíveis */}
          <div style={{
            backgroundColor: COR.vermBg, border: `1px solid ${COR.vermelho}`,
            borderRadius: '6px', padding: '10px 12px', marginBottom: '10px',
          }}>
            <div style={{ fontWeight: '700', color: COR.vermelho, marginBottom: '4px' }}>
              {excluiveis.length === 0
                ? 'Nada será excluído (não há Planejadas/Correção)'
                : `Serão EXCLUÍDOS ${excluiveis.length} lançamento(s):`}
            </div>
            {excluiveis.length > 0 && (
              <div style={{ fontSize: '12px', color: COR.texto, marginTop: '4px' }}>
                {excluiveis.map(l => (
                  <span key={l.id} style={{
                    display: 'inline-block', backgroundColor: COR.branco,
                    border: `1px solid ${COR.vermelho}`, borderRadius: '3px',
                    padding: '2px 6px', margin: '2px 4px 2px 0',
                  }} title={`${l.situacao === 'P' ? 'Planejada' : 'Correção'}`}>
                    {formatDataHora(l)} <span style={{ color: COR.muted }}>({l.situacao})</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Preservados */}
          {preservados.length > 0 && (
            <div style={{
              backgroundColor: COR.verdeBg, border: `1px solid ${COR.verde}`,
              borderRadius: '6px', padding: '10px 12px',
            }}>
              <div style={{ fontWeight: '700', color: COR.verde, marginBottom: '4px' }}>
                Serão PRESERVADOS {preservados.length} lançamento(s):
              </div>
              <div style={{ fontSize: '12px', color: COR.texto, marginTop: '4px' }}>
                {preservados.map(l => (
                  <span key={l.id} style={{
                    display: 'inline-block', backgroundColor: COR.branco,
                    border: `1px solid ${COR.verde}`, borderRadius: '3px',
                    padding: '2px 6px', margin: '2px 4px 2px 0',
                  }} title={l.situacao === 'X' ? 'Executada' : 'Fechada'}>
                    {formatDataHora(l)} <span style={{ color: COR.muted }}>({l.situacao})</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <p style={{ marginTop: '14px', fontSize: '12px', color: COR.muted, fontStyle: 'italic' }}>
            Apenas lançamentos em situação <strong>Planejada</strong> ou <strong>Correção</strong> podem
            ser excluídos. <strong>Executadas</strong> e <strong>Fechadas</strong> são preservadas.
          </p>
        </div>
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
                       display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onCancelar}
            style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                     backgroundColor: COR.branco, color: COR.muted,
                     border: `1px solid ${COR.borda}`, borderRadius: '4px', cursor: 'pointer' }}>
            Cancelar
          </button>
          <button onClick={onConfirmar} disabled={excluiveis.length === 0}
            style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                     backgroundColor: excluiveis.length === 0 ? COR.muted : COR.vermelho,
                     color: COR.branco, border: 'none', borderRadius: '4px',
                     cursor: excluiveis.length === 0 ? 'not-allowed' : 'pointer',
                     opacity: excluiveis.length === 0 ? 0.5 : 1 }}>
            Excluir
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── <ModalEscopoExclusao> r75 ──────────────────────────────────────────────
// Disparado quando há excluíveis FORA do intervalo dataInicio..dataFim do form.
// Pergunta se deve limitar pelo período do form ou excluir todos.
function ModalEscopoExclusao({ qtdNoPeriodo, qtdForaPeriodo, dataInicio, dataFim, onEscolha, onCancelar }) {
  if (qtdNoPeriodo === undefined && qtdForaPeriodo === undefined) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onCancelar}>
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: COR.branco, borderRadius: '8px',
        width: '520px', maxWidth: '90%',
        fontFamily: "'Manrope', sans-serif",
      }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${COR.borda}`,
                       fontSize: '15px', fontWeight: '700', color: COR.azul }}>
          📅 Escopo da exclusão
        </div>
        <div style={{ padding: '14px 18px', fontSize: '13px', color: COR.texto }}>
          <p style={{ marginTop: 0 }}>
            Este projeto tem lançamentos <strong>excluíveis</strong> além do período do formulário
            ({ddmmaaaa(dataInicio)} a {ddmmaaaa(dataFim)}).
          </p>
          <ul style={{ marginLeft: '20px', paddingLeft: 0 }}>
            <li><strong>{qtdNoPeriodo}</strong> dentro do período do form</li>
            <li><strong>{qtdForaPeriodo}</strong> fora do período (anteriores ou posteriores)</li>
          </ul>
          <p style={{ marginTop: '12px' }}>
            Como deseja proceder?
          </p>
        </div>
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
                       display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={onCancelar}
            style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                     backgroundColor: COR.branco, color: COR.muted,
                     border: `1px solid ${COR.borda}`, borderRadius: '4px', cursor: 'pointer' }}>
            Cancelar
          </button>
          <button onClick={() => onEscolha('PERIODO')}
            style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                     backgroundColor: COR.branco, color: COR.azul,
                     border: `1px solid ${COR.azul}`, borderRadius: '4px', cursor: 'pointer' }}>
            Apenas do período ({qtdNoPeriodo})
          </button>
          <button onClick={() => onEscolha('TODOS')}
            style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                     backgroundColor: COR.vermelho, color: COR.branco,
                     border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Todos ({qtdNoPeriodo + qtdForaPeriodo})
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════════════════════
function PlanejamentoModal({ analistaInicial, onFechar }) {
  const { usuario } = useAuth();
  const ehAdminUsuario = (usuario?.perfil || '').toLowerCase() === 'administrador';

  // r67: rastreia se algum lançamento foi criado durante a vida do modal.
  const [criouAlgo, setCriouAlgo] = useState(false);

  // Form do topo (r71: removido 'periodo')
  // r73 item 1: Data Início/Fim default = 1º e último dia do mês atual
  const _hoje = new Date();
  const _anoIni = _hoje.getFullYear();
  const _mesIni = _hoje.getMonth() + 1;
  const _ultDia = new Date(_anoIni, _mesIni, 0).getDate();
  const _mmAtual = String(_mesIni).padStart(2, '0');
  const _defaultIni = `${_anoIni}-${_mmAtual}-01`;
  const _defaultFim = `${_anoIni}-${_mmAtual}-${String(_ultDia).padStart(2, '0')}`;

  const [analista, setAnalista] = useState({ id: '', label: '' });
  const [dataInicio, setDataInicio] = useState(_defaultIni);
  const [dataFim, setDataFim] = useState(_defaultFim);
  const [horaInicio, setHoraInicio] = useState('09:00');
  const [qtdHoras, setQtdHoras] = useState('08:00');
  const [almoco, setAlmoco] = useState('01:00');
  const [deslocamento, setDeslocamento] = useState('00:00');
  const [projeto, setProjeto] = useState({ id: '', label: '', cliente_fantasia: '', tipo: '' });
  const [cliente, setCliente] = useState({ id: '', label: '' });
  const [tipo, setTipo] = useState('');
  const [alocacao, setAlocacao] = useState('DIAS_SEMANA');
  const [diasSemanaSel, setDiasSemanaSel] = useState([]);  // r72 item 9: desmarcados por default

  // r71: viewAno/viewMes controlam o calendário (independente do form)
  const hoje = new Date();
  const [viewAno, setViewAno] = useState(hoje.getFullYear());
  const [viewMes, setViewMes] = useState(hoje.getMonth() + 1);

  // r74: 'cor' do projeto selecionado é calculada depois (via mapaCoresProjeto)
  //      junto com a alocação inteligente. Veja useMemo mais abaixo.

  // r72 helpers de data ─────────────────────────────────────────────────────
  // ultimoDiaDoMes('2026-06-15') → '2026-06-30'
  const ultimoDiaDoMes = (dataStr) => {
    const m = String(dataStr).match(/^(\d{4})-(\d{2})/);
    if (!m) return dataStr;
    const ano = parseInt(m[1], 10);
    const mes = parseInt(m[2], 10);
    const dia = new Date(ano, mes, 0).getDate();
    return `${m[1]}-${m[2]}-${String(dia).padStart(2, '0')}`;
  };

  // clampDiaDoMes('2026-06-31') → '2026-06-30'   (item 3)
  // Se o dia digitado ultrapassa o último dia do mês, ajusta pro último.
  // O input type="date" geralmente já bloqueia, mas como o usuário pode
  // digitar via teclado em alguns navegadores, garantimos no JS.
  const clampDiaDoMes = (dataStr) => {
    const m = String(dataStr).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return dataStr;
    const ano = parseInt(m[1], 10);
    const mes = parseInt(m[2], 10);
    const dia = parseInt(m[3], 10);
    const ultDia = new Date(ano, mes, 0).getDate();
    if (dia > ultDia) {
      return `${m[1]}-${m[2]}-${String(ultDia).padStart(2, '0')}`;
    }
    return dataStr;
  };

  // r72 item 5: ao mudar data início, preenche data fim:
  //   - se dataFim está vazia, OU
  //   - se nova dataInicio > dataFim atual
  // Em ambos casos: dataFim = último dia do mês da nova dataInicio
  const handleDataInicio = (novaIni) => {
    const clamped = clampDiaDoMes(novaIni);  // r72 item 3
    setDataInicio(clamped);
    if (!clamped) return;
    if (!dataFim || clamped > dataFim) {
      setDataFim(ultimoDiaDoMes(clamped));
    }
  };

  const handleDataFim = (novoFim) => {
    setDataFim(clampDiaDoMes(novoFim));  // r72 item 3
  };

  // Listas
  const [analistas, setAnalistas] = useState([]);

  // Estado de salvamento
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');

  // Modais aninhados
  const [feriadosPendentes, setFeriadosPendentes] = useState(null);
  const [feriadoAcoes, setFeriadoAcoes] = useState({});
  const [conflitosPendentes, setConflitosPendentes] = useState(null);
  const [previewCarregado, setPreviewCarregado] = useState(null);

  // Calendário do mês de visualização
  const [calendario, setCalendario] = useState([]);
  const [horarioColab, setHorarioColab] = useState(null);

  // r70: grid / filtro
  const [projetosOcultos, setProjetosOcultos] = useState(new Set());
  // r72 item 7: state e funções de edição removidos (sem mais botão ✏ na grid)
  const [excluindoGrupo, setExcluindoGrupo] = useState(null);
  // r75: modais de confirmação e escopo da exclusão
  const [confirmacaoExclusao, setConfirmacaoExclusao] = useState(null);
  const [escopoExclusao, setEscopoExclusao] = useState(null);

  // ─── Inicialização: pré-seleciona analista conforme regra (r67) ────────
  useEffect(() => {
    (async () => {
      try {
        const lista = await lancamentoHorasService.buscarColaboradores('');
        setAnalistas(lista);

        if (analistaInicial?.id) {
          setAnalista(analistaInicial);
        } else if (!ehAdminUsuario) {
          const meu = lista.find(c => c.usuario_id === usuario.id);
          if (meu) setAnalista({ id: meu.id, label: meu.nome_amigavel || meu.nome });
        }
      } catch (err) {
        console.warn('Falha carregar analistas:', err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carrega calendário + horário do colaborador (usa viewAno/viewMes)
  const carregarCalendario = useCallback(async () => {
    if (!analista.id) {
      setCalendario([]); setHorarioColab(null); return;
    }
    try {
      const r = await planejamentoHorasService.calendario({
        usuario_id: usuario.id,
        colaborador_id: analista.id,
        ano: viewAno,
        mes: viewMes,
      });
      setCalendario(r?.lancamentos || []);
      setHorarioColab(r?.horario_colaborador || null);
    } catch (err) {
      console.warn('Falha calendário:', err.message);
    }
  }, [analista.id, viewAno, viewMes, usuario?.id]);
  useEffect(() => { carregarCalendario(); }, [carregarCalendario]);

  // r70: agrupa lançamentos por projeto
  // r74: mapa de cor por projeto — alocação inteligente baseada nos projetos
  //      VISÍVEIS no calendário + o projeto selecionado no form (caso novo).
  //      Recalcula quando a lista de projetos visíveis muda (ex: filtra).
  //      Estável: ordena por projeto_id antes de alocar.
  const mapaCoresProjeto = useMemo(() => {
    const ids = new Set();
    (calendario || []).forEach(l => { if (l.projeto_id) ids.add(l.projeto_id); });
    if (projeto.id) ids.add(projeto.id);  // inclui o que o usuário tá montando
    return alocarCoresProjetos([...ids]);
  }, [calendario, projeto.id]);

  // r74: cor do projeto SELECIONADO no form (mostrada no quadradinho da L3)
  const cor = useMemo(() => {
    if (!projeto.id) return '#cbd5e1';
    return mapaCoresProjeto.get(projeto.id) || corDoProjeto(projeto.id);
  }, [projeto.id, mapaCoresProjeto]);

  const gruposPorProjeto = useMemo(() => {
    const map = new Map();
    for (const l of (calendario || [])) {
      const key = l.projeto_id || '(sem projeto)';
      if (!map.has(key)) {
        map.set(key, {
          projeto_id:        l.projeto_id || '',
          projeto_descricao: l.projeto_descricao || '',
          cliente_id:        l.cliente_id || '',
          cliente_fantasia:  l.cliente_fantasia || '',
          tipo:              l.tipo || '',
          lancamentos:       [],
        });
      }
      map.get(key).lancamentos.push(l);
    }
    const arr = [];
    for (const g of map.values()) {
      let horasPlan = 0, horasExec = 0, horasCorr = 0;
      let temPlanejada = false, temExecutada = false, temCorrecao = false, temFechada = false;
      for (const l of g.lancamentos) {
        const q = parseFloat(l.qtd_horas) || 0;
        if (l.situacao === 'P') { horasPlan += q; temPlanejada = true; }
        else if (l.situacao === 'X') { horasExec += q; temExecutada = true; }
        else if (l.situacao === 'C') { horasCorr += q; temCorrecao = true; }
        else if (l.situacao === 'F') { temFechada = true; }
      }
      g.lancamentos.sort((a, b) => String(a.data).localeCompare(String(b.data)));
      arr.push({
        ...g,
        horas_planejadas: horasPlan,
        horas_executadas: horasExec,
        horas_correcao:   horasCorr,                 // r76: nova coluna
        horas_total:      horasPlan + horasExec + horasCorr,  // r76: inclui C
        tem_planejada:    temPlanejada,
        tem_executada:    temExecutada,
        tem_correcao:     temCorrecao,
        tem_fechada:      temFechada,
        // r75: exclu\u00edvel = tem pelo menos 1 P ou 1 C
        tem_excluivel:    temPlanejada || temCorrecao,
        // r74: cor agora vem da alocação inteligente (não do hash isolado)
        cor:              mapaCoresProjeto.get(g.projeto_id) || corDoProjeto(g.projeto_id),
        qtd_lancamentos:  g.lancamentos.length,
      });
    }
    arr.sort((a, b) => {
      const c = (a.cliente_fantasia || '').localeCompare(b.cliente_fantasia || '');
      if (c !== 0) return c;
      return (a.projeto_id || '').localeCompare(b.projeto_id || '');
    });
    return arr;
  }, [calendario, mapaCoresProjeto]);

  // r74: aplica a cor do mapa em cada lançamento antes de passar pro
  //      calendário 2D. Ignora cor_personalizada salva no banco em prol da
  //      consistência visual com a alocação inteligente.
  const calendarioComCor = useMemo(() => {
    return (calendario || []).map(l => ({
      ...l,
      cor_personalizada: mapaCoresProjeto.get(l.projeto_id) || corDoProjeto(l.projeto_id),
    }));
  }, [calendario, mapaCoresProjeto]);

  const calendarioVisivel = useMemo(() => {
    if (projetosOcultos.size === 0) return calendarioComCor;
    return calendarioComCor.filter(l => !projetosOcultos.has(l.projeto_id || '(sem projeto)'));
  }, [calendarioComCor, projetosOcultos]);

  // ─── Buscas de combobox ────────────────────────────────────────────────
  const buscarAnalistas = async (q) => {
    const qLow = (q || '').toLowerCase();
    return analistas
      .filter(c => !qLow || (c.nome_amigavel || c.nome || '').toLowerCase().includes(qLow))
      .slice(0, 30)
      .map(c => ({ value: c.id, label: c.nome_amigavel || c.nome, raw: c }));
  };

  const buscarClientes = async (q) => {
    // r167c: ANTES chamava clienteService.listar() → GET /clientes, que exige
    // permissão de Clientes → 403 pra quem planeja sem acesso ao cadastro.
    // Agora usa a referência leve (qualquer autenticado), filtrando no servidor (?q=).
    const lista = await clienteService.listarReferencia(q);
    return (Array.isArray(lista) ? lista : [])
      .slice(0, 30)
      .map(c => ({
        value: c.id,
        label: c.nome_fantasia || c.razao_social || c.id,   // r71 item 8: nome fantasia
        raw: c,
      }));
  };

  const buscarProjetos = async (q) => {
    const lista = await lancamentoHorasService.projetosDoCliente(cliente.id || '', q);
    // r71 item 10: enriquece com cliente fantasia (vem como cliente_label do backend)
    return (Array.isArray(lista) ? lista : []).map(p => ({
      value: p.id,
      label: `📂 ${p.id} - ${p.descricao || ''}`,
      cliente_fantasia: p.cliente_label || p.cliente_fantasia || p.cliente_nome || '',
      raw: p,
    }));
  };

  const aoSelecionarProjeto = async (op) => {
    setProjeto({
      id: op.value, label: op.label,
      cliente_fantasia: op.cliente_fantasia || '',
      tipo: '',
    });
    try {
      const det = await lancamentoHorasService.projetoLookup(op.value);
      const tipoLanc = MAP_TIPO_NEG_PRA_LANC[det.tipo] || '';
      setTipo(tipoLanc);
      if (det.cliente_id && det.cliente_id !== cliente.id) {
        setCliente({
          id: det.cliente_id,
          // r72 item 8: o backend retorna 'cliente_label' (COALESCE nome_fantasia, razao_social)
          label: det.cliente_label || det.cliente_id,
        });
      }
    } catch (err) {
      console.warn(err);
    }
  };

  // r73 item 2: ao trocar cliente, limpa o projeto pra forçar nova seleção
  //             (assim o combo de Projeto vai filtrar pelos do novo cliente)
  const aoSelecionarCliente = (op) => {
    setCliente({ id: op.value, label: op.label });
    if (projeto.id) {
      setProjeto({ id: '', label: '', cliente_fantasia: '', tipo: '' });
      setTipo('');
    }
  };

  const toggleDiaSemana = (v) => {
    if (diasSemanaSel.includes(v)) setDiasSemanaSel(diasSemanaSel.filter(x => x !== v));
    else setDiasSemanaSel([...diasSemanaSel, v].sort());
  };

  const validar = () => {
    if (!analista.id) return 'Selecione um analista.';
    if (!dataInicio || !dataFim) return 'Informe data início e fim.';
    if (dataInicio > dataFim) return 'Data início não pode ser depois da data fim.';
    if (!projeto.id) return 'Selecione um projeto.';
    if (!horaInicio) return 'Informe a hora de início.';
    if (hhmmToDec(qtdHoras) <= 0) return 'Quantidade de horas deve ser maior que zero.';
    if (alocacao === 'DIAS_SEMANA' && diasSemanaSel.length === 0)
      return 'Selecione ao menos um dia da semana.';
    return null;
  };

  const aoClicarSalvar = async () => {
    setErro(''); setAviso('');
    const v = validar();
    if (v) { setErro(v); return; }
    try {
      setSalvando(true);
      const alocacaoPayload = {
        data_inicio: dataInicio, data_fim: dataFim,
        hora_inicio: horaInicio,
        qtd_horas:   hhmmToDec(qtdHoras),
        intervalo:   hhmmToDec(almoco),
        deslocamento: hhmmToDec(deslocamento),  // r71: novo (backend ignora por enquanto)
        projeto_id:  projeto.id,
        cliente_id:  cliente.id,
        tipo,
        alocacao,
        dias_semana: diasSemanaSel,
        cor_personalizada: cor,
        feriado_acoes: {},
      };
      const r = await planejamentoHorasService.preview({
        usuario_id: usuario.id,
        colaborador_id: analista.id,
        alocacoes: [alocacaoPayload],
      });
      setPreviewCarregado({ r, alocacaoPayload });

      const feriadosCand = r.resumo?.[0]?.feriados_candidatos || [];
      if (feriadosCand.length > 0) {
        const acoes = {};
        feriadosCand.forEach(f => { acoes[f.data] = 'PULAR'; });
        setFeriadoAcoes(acoes);
        setFeriadosPendentes(feriadosCand);
      } else if (r.conflitos && r.conflitos.length > 0) {
        setConflitosPendentes(r.conflitos);
      } else {
        await persistir(r.blocos);
      }
    } catch (err) {
      setErro('Falha no preview: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setSalvando(false);
    }
  };

  const persistir = async (blocos) => {
    try {
      setSalvando(true);
      const r = await planejamentoHorasService.salvar({
        usuario_id: usuario.id,
        colaborador_id: analista.id,
        blocos,
      });
      setAviso(`${r.qtd_criados || blocos.length} lançamento(s) criado(s) com sucesso.`);
      setCriouAlgo(true);
      setFeriadosPendentes(null);
      setConflitosPendentes(null);
      setPreviewCarregado(null);
      // r71: posiciona o calendário no mês da data de início, pra ver o resultado
      const m = String(dataInicio).match(/^(\d{4})-(\d{2})/);
      if (m) {
        setViewAno(parseInt(m[1], 10));
        setViewMes(parseInt(m[2], 10));
      }
      carregarCalendario();
    } catch (err) {
      setErro('Erro ao salvar: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setSalvando(false);
    }
  };

  const confirmarFeriados = async () => {
    setFeriadosPendentes(null);
    try {
      setSalvando(true);
      const aloc = { ...previewCarregado.alocacaoPayload, feriado_acoes: feriadoAcoes };
      const r = await planejamentoHorasService.preview({
        usuario_id: usuario.id,
        colaborador_id: analista.id,
        alocacoes: [aloc],
      });
      setPreviewCarregado({ r, alocacaoPayload: aloc });
      if (r.conflitos && r.conflitos.length > 0) {
        setConflitosPendentes(r.conflitos);
      } else {
        await persistir(r.blocos);
      }
    } catch (err) {
      setErro('Erro no preview: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setSalvando(false);
    }
  };

  const confirmarConflitos = async () => {
    setConflitosPendentes(null);
    if (previewCarregado?.r?.blocos) {
      await persistir(previewCarregado.r.blocos);
    }
  };

  // r70: ações da grid
  const toggleProjetoOculto = (projetoId) => {
    setProjetosOcultos(prev => {
      const novo = new Set(prev);
      if (novo.has(projetoId)) novo.delete(projetoId);
      else novo.add(projetoId);
      return novo;
    });
  };

  // r75: clique no 🗑 da grid — abre modal de confirmação. NÃO exclui de cara.
  const excluirGrupo = (grupo) => {
    const excluiveis  = grupo.lancamentos.filter(l => l.situacao === 'P' || l.situacao === 'C');
    const preservados = grupo.lancamentos.filter(l => l.situacao === 'X' || l.situacao === 'F');
    setConfirmacaoExclusao({ grupo, excluiveis, preservados });
  };

  // r75: usuário confirmou no Modal 1. Decidir se vai precisar do Modal 2
  //      (escopo) ou se já pode ir pra exclusão direta.
  const aoConfirmarExclusao = () => {
    const { grupo, excluiveis } = confirmacaoExclusao;
    setConfirmacaoExclusao(null);
    if (excluiveis.length === 0) return;  // botão estava desabilitado, mas guarda

    // Tem período válido pra filtrar?
    const temPeriodoValido = dataInicio && dataFim && dataInicio <= dataFim;
    if (!temPeriodoValido) {
      // Sem período válido, exclui tudo direto
      persistirExclusao(grupo, excluiveis);
      return;
    }

    // Separa em "dentro" x "fora" do período do form
    const dentro = excluiveis.filter(l => l.data >= dataInicio && l.data <= dataFim);
    const fora   = excluiveis.filter(l => !(l.data >= dataInicio && l.data <= dataFim));

    if (fora.length === 0) {
      // Tudo cai no período do form — não precisa perguntar
      persistirExclusao(grupo, excluiveis);
      return;
    }

    // Tem coisas fora — abre modal de escopo
    setEscopoExclusao({
      grupo,
      excluiveisNoPeriodo:  dentro,
      excluiveisForaPeriodo: fora,
    });
  };

  // r75: usuário escolheu no Modal 2 (escopo)
  const aoEscolherEscopo = (escolha) => {
    const { grupo, excluiveisNoPeriodo, excluiveisForaPeriodo } = escopoExclusao;
    setEscopoExclusao(null);
    const lista = escolha === 'TODOS'
      ? [...excluiveisNoPeriodo, ...excluiveisForaPeriodo]
      : excluiveisNoPeriodo;
    persistirExclusao(grupo, lista);
  };

  // r75: chamada efetiva ao backend (loop de DELETE)
  const persistirExclusao = async (grupo, lista) => {
    if (lista.length === 0) {
      setAviso('Nada a excluir.');
      return;
    }
    try {
      setExcluindoGrupo(grupo.projeto_id);
      let okCount = 0, errCount = 0, primeiroErro = null;
      for (const l of lista) {
        try {
          await lancamentoHorasService.excluir(l.id, usuario.id);
          okCount++;
        } catch (err) {
          errCount++;
          if (!primeiroErro) primeiroErro = err?.response?.data?.mensagem || err.message;
        }
      }
      if (errCount === 0) {
        setAviso(`${okCount} lançamento(s) excluído(s).`);
      } else if (okCount === 0) {
        setErro(`Falha ao excluir: ${primeiroErro}`);
      } else {
        setAviso(`${okCount} excluído(s), ${errCount} falhou(aram). ${primeiroErro ? 'Primeiro erro: ' + primeiroErro : ''}`);
      }
      setCriouAlgo(true);
      await carregarCalendario();
    } catch (err) {
      setErro('Erro ao excluir: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setExcluindoGrupo(null);
    }
  };

  const fechar = () => onFechar(criouAlgo);

  // r71: renderer customizado pra projeto (ID-Desc em cima, fantasia embaixo)
  const renderItemProjeto = (op) => (
    <div>
      <div style={{ color: COR.texto, fontSize: '13px' }}>{op.label}</div>
      {op.cliente_fantasia && (
        <div style={{ color: COR.muted, fontSize: '11px', marginTop: '1px' }}>
          {op.cliente_fantasia}
        </div>
      )}
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      fontFamily: "'Manrope', sans-serif",
    }}>
      <div style={{
        backgroundColor: COR.branco, borderRadius: '8px',
        width: '95vw', maxWidth: '1400px', height: '95vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px', backgroundColor: COR.azul, color: COR.branco,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: '15px', fontWeight: '600' }}>
            📅 Planejamento de Alocação
          </div>
          <button onClick={fechar}
            style={{
              width: '28px', height: '28px', border: 'none',
              backgroundColor: 'rgba(255,255,255,0.15)', color: COR.branco,
              borderRadius: '4px', fontSize: '16px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }} title="Fechar">×</button>
        </div>

        {/* Corpo rolante */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {erro && (
            <div style={{
              padding: '10px 14px', marginBottom: '10px',
              backgroundColor: COR.vermBg, color: COR.vermelho,
              border: `1px solid ${COR.vermelho}`, borderRadius: '6px',
              fontSize: '12px', fontWeight: '600',
            }}>{erro}</div>
          )}
          {aviso && (
            <div style={{
              padding: '10px 14px', marginBottom: '10px',
              backgroundColor: COR.verdeBg, color: COR.verde,
              border: `1px solid ${COR.verde}`, borderRadius: '6px',
              fontSize: '12px', fontWeight: '600',
            }}>{aviso}</div>
          )}

          {/* ─── Form 3 linhas (r71) ────────────────────────────────────── */}
          <div style={{
            backgroundColor: COR.branco, border: `1px solid ${COR.borda}`,
            borderRadius: '8px', padding: '14px', marginBottom: '12px',
          }}>
            {/* r72 item 2 — L1: Analista, Data Início, Data Fim, Hora Início, Qtd Horas, Almoço */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: '10px' }}>
              <div>
                <label style={labelStyle}>Analista</label>
                <Combobox
                  label={analista.label}
                  placeholder="Selecione..."
                  disabled={!ehAdminUsuario && !!analista.id}
                  buscar={buscarAnalistas}
                  onSelecionar={op => setAnalista({ id: op.value, label: op.label })}
                />
              </div>
              <div>
                <label style={labelStyle}>Data Início</label>
                <input type="date" value={dataInicio} onChange={e => handleDataInicio(e.target.value)}
                       style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Data Fim</label>
                <input type="date" value={dataFim} onChange={e => handleDataFim(e.target.value)}
                       style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Hora Início</label>
                <input type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)}
                       style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Qtd. Horas</label>
                <input type="time" value={qtdHoras} onChange={e => setQtdHoras(e.target.value)}
                       style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Almoço</label>
                <input type="time" value={almoco} onChange={e => setAlmoco(e.target.value)}
                       style={inputStyle} />
              </div>
            </div>

            {/* r72 item 2 — L2: Projeto, Cliente, Tipo, Deslocamento */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', gap: '10px', marginTop: '10px' }}>
              <div>
                <label style={labelStyle}>Projeto</label>
                <Combobox
                  label={projeto.label}
                  placeholder={cliente.id ? 'Projetos do cliente...' : 'Buscar projeto...'}
                  buscar={buscarProjetos}
                  onSelecionar={aoSelecionarProjeto}
                  renderItem={renderItemProjeto}
                />
              </div>
              <div>
                <label style={labelStyle}>Cliente</label>
                <Combobox
                  label={cliente.label}
                  placeholder="Cliente..."
                  buscar={buscarClientes}
                  onSelecionar={aoSelecionarCliente}
                  renderItem={(op) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <TagCliente tag={op.raw?.tag} cor={op.raw?.cor_tag} />
                      <span>{op.label}</span>
                    </div>
                  )}
                />
              </div>
              <div>
                <label style={labelStyle}>Tipo</label>
                <input type="text" value={TIPO_LANC_LABEL[tipo] || '—'} readOnly
                       style={{ ...inputStyle, backgroundColor: COR.cinzaBg, color: COR.muted }} />
              </div>
              <div>
                <label style={labelStyle}>Deslocamento</label>
                <input type="time" value={deslocamento} onChange={e => setDeslocamento(e.target.value)}
                       style={inputStyle} />
              </div>
            </div>

            {/* r72 item 2 — L3: Alocação, Dias da Semana (flex 1, justify-between
                quando ativo), Cor (posição fixa), Botão Salvar */}
            <div style={{
              display: 'flex', alignItems: 'flex-end', gap: '10px', marginTop: '10px',
            }}>
              <div style={{ flexShrink: 0 }}>
                <label style={labelStyle}>Alocação</label>
                <select value={alocacao} onChange={e => setAlocacao(e.target.value)}
                        style={{ ...inputStyle, width: '160px' }}>
                  <option value="INTEGRAL">Integral</option>
                  <option value="DIAS_SEMANA">Dias da Semana</option>
                </select>
              </div>
              {alocacao === 'DIAS_SEMANA' ? (
                <div style={{
                  display: 'flex', flex: 1,
                  justifyContent: 'space-around',  // r72: preenche toda a largura
                  paddingBottom: '7px',
                }}>
                  {DIAS_SEMANA_OPCOES.map(d => (
                    <label key={d.v} style={{
                      display: 'flex', alignItems: 'center', gap: '4px',
                      fontSize: '12px', cursor: 'pointer',
                    }}>
                      <input type="checkbox" checked={diasSemanaSel.includes(d.v)}
                             onChange={() => toggleDiaSemana(d.v)} />
                      {d.label}
                    </label>
                  ))}
                </div>
              ) : (
                <div style={{ flex: 1 }} />
              )}

              {/* r72 item 2: Cor em posição fixa (sempre ao lado do botão) */}
              <div style={{ flexShrink: 0 }}>
                <label style={labelStyle}>Cor</label>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '6px',
                  backgroundColor: cor, border: `1px solid ${COR.borda}`,
                }} title={`Cor automática do projeto ${projeto.id || '(selecione)'}`} />
              </div>

              {/* Botão Salvar como ícone quadrado */}
              <SePode rotina="lancamentos-horas" acao="planejar">
              <button onClick={aoClicarSalvar} disabled={salvando}
                title={salvando ? 'Processando...' : 'Salvar Planejamento'}
                style={{
                  width: '36px', height: '32px',
                  backgroundColor: COR.azul, color: COR.branco,
                  border: 'none', borderRadius: '4px',
                  cursor: salvando ? 'not-allowed' : 'pointer',
                  opacity: salvando ? 0.6 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
              </button>
              </SePode>
            </div>
          </div>

          {/* ─── Grid de alocações por projeto (r70) ──────────────────── */}
          {analista.id && gruposPorProjeto.length > 0 && (
            <div style={{
              backgroundColor: COR.branco, border: `1px solid ${COR.borda}`,
              borderRadius: '8px', overflow: 'hidden', marginBottom: '12px',
            }}>
              <div style={{
                padding: '10px 14px', borderBottom: `1px solid ${COR.borda}`,
                backgroundColor: COR.cinzaBg, fontSize: '13px', fontWeight: '700', color: COR.texto,
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                Alocações
                <span style={{ fontSize: '11px', fontWeight: '400', color: COR.muted }}>
                  {gruposPorProjeto.length} projeto(s) · desmarque pra ocultar do calendário
                </span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: COR.azul, color: COR.branco }}>
                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', width: '36px' }}></th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', width: '24px' }}></th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600' }}>Cliente</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600' }}>Projeto</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600' }}>Tipo</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600' }}>Planejadas</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600' }}>Correção</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600' }}>Executadas</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600' }}>Total</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {gruposPorProjeto.map((g, i) => {
                    const oculto = projetosOcultos.has(g.projeto_id || '(sem projeto)');
                    // r75: pode excluir se tem PLANEJADAS OU CORREÇÃO
                    const podeExcluir = g.tem_excluivel;
                    return (
                      <tr key={g.projeto_id || `_${i}`} style={{
                        borderBottom: `1px solid ${COR.borda}`,
                        backgroundColor: i % 2 === 0 ? COR.branco : COR.cinzaBg,
                        opacity: oculto ? 0.5 : 1,
                      }}>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={!oculto}
                            onChange={() => toggleProjetoOculto(g.projeto_id || '(sem projeto)')}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{
                            display: 'inline-block', width: '14px', height: '14px',
                            backgroundColor: g.cor, borderRadius: '3px',
                            border: `1px solid rgba(0,0,0,0.08)`,
                          }} />
                        </td>
                        <td style={{ padding: '8px 10px', color: COR.texto }}>
                          {g.cliente_fantasia || '—'}
                        </td>
                        <td style={{ padding: '8px 10px', color: COR.texto, maxWidth: '300px',
                                     overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={`${g.projeto_id}${g.projeto_descricao ? ' — ' + g.projeto_descricao : ''}`}>
                          {g.projeto_id}{g.projeto_descricao && ` — ${g.projeto_descricao}`}
                        </td>
                        <td style={{ padding: '8px 10px', color: COR.texto }}>
                          {TIPO_LANC_LABEL[g.tipo] || g.tipo || '—'}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: COR.texto }}>
                          {String(Math.floor(g.horas_planejadas)).padStart(2, '0')}:{String(Math.round((g.horas_planejadas - Math.floor(g.horas_planejadas)) * 60)).padStart(2, '0')}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right',
                                     color: g.horas_correcao > 0 ? COR.amarelo : COR.muted,
                                     fontWeight: g.horas_correcao > 0 ? '700' : '400' }}>
                          {String(Math.floor(g.horas_correcao)).padStart(2, '0')}:{String(Math.round((g.horas_correcao - Math.floor(g.horas_correcao)) * 60)).padStart(2, '0')}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: COR.texto }}>
                          {String(Math.floor(g.horas_executadas)).padStart(2, '0')}:{String(Math.round((g.horas_executadas - Math.floor(g.horas_executadas)) * 60)).padStart(2, '0')}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: COR.texto, fontWeight: '700' }}>
                          {String(Math.floor(g.horas_total)).padStart(2, '0')}:{String(Math.round((g.horas_total - Math.floor(g.horas_total)) * 60)).padStart(2, '0')}
                        </td>
                        {/* r72 item 7: coluna ✏ removida */}
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                          <button
                            onClick={() => excluirGrupo(g)}
                            disabled={!podeExcluir || excluindoGrupo === g.projeto_id}
                            title={(() => {
                              if (!podeExcluir) return 'Nenhum lançamento Planejado/Correção pra excluir';
                              const qtd = g.lancamentos.filter(l => l.situacao === 'P' || l.situacao === 'C').length;
                              return `Excluir ${qtd} lançamento(s) Planejado(s)/Correção`;
                            })()}
                            style={{
                              border: 'none', backgroundColor: 'transparent',
                              cursor: podeExcluir ? 'pointer' : 'not-allowed',
                              color: podeExcluir ? COR.vermelho : COR.muted,
                              opacity: podeExcluir ? 1 : 0.4,
                              fontSize: '14px', padding: '2px 4px',
                            }}>
                            {excluindoGrupo === g.projeto_id ? '⏳' : '🗑'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ─── Calendário visual 2D (r68/r71) ─────────────────────────── */}
          {analista.id ? (
            <CalendarioMensal
              ano={viewAno}
              mes={viewMes}
              lancamentos={calendarioVisivel}
              horarioColab={horarioColab}
              onNavegar={(na, nm) => { setViewAno(na); setViewMes(nm); }}
            />
          ) : (
            <div style={{
              backgroundColor: COR.branco, border: `1px solid ${COR.borda}`,
              borderRadius: '8px', padding: '30px', textAlign: 'center',
              color: COR.muted, fontStyle: 'italic',
            }}>
              Selecione um analista pra visualizar o calendário.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
          backgroundColor: COR.cinzaBg,
        }}>
          <button onClick={fechar}
            style={{
              padding: '8px 20px', fontSize: '13px', fontWeight: '600',
              backgroundColor: COR.branco, color: COR.texto,
              border: `1px solid ${COR.borda}`, borderRadius: '4px',
              cursor: 'pointer',
            }}>
            Fechar
          </button>
        </div>
      </div>

      {/* Modais aninhados */}
      <ModalFeriados
        feriados={feriadosPendentes}
        valores={feriadoAcoes}
        onChange={(data, acao) => setFeriadoAcoes(f => ({ ...f, [data]: acao }))}
        onConfirmar={confirmarFeriados}
        onCancelar={() => { setFeriadosPendentes(null); setPreviewCarregado(null); setSalvando(false); }}
      />
      <ModalConflitos
        conflitos={conflitosPendentes}
        onConfirmar={confirmarConflitos}
        onCancelar={() => { setConflitosPendentes(null); setPreviewCarregado(null); setSalvando(false); }}
      />
      {/* r75: confirma\u00e7\u00e3o de exclus\u00e3o com lista de exclu\u00edveis/preservados */}
      {confirmacaoExclusao && (
        <ModalConfirmaExclusao
          grupo={confirmacaoExclusao.grupo}
          excluiveis={confirmacaoExclusao.excluiveis}
          preservados={confirmacaoExclusao.preservados}
          onConfirmar={aoConfirmarExclusao}
          onCancelar={() => setConfirmacaoExclusao(null)}
        />
      )}
      {/* r75: escolha de escopo (per\u00edodo do form ou todos) */}
      {escopoExclusao && (
        <ModalEscopoExclusao
          qtdNoPeriodo={escopoExclusao.excluiveisNoPeriodo.length}
          qtdForaPeriodo={escopoExclusao.excluiveisForaPeriodo.length}
          dataInicio={dataInicio}
          dataFim={dataFim}
          onEscolha={aoEscolherEscopo}
          onCancelar={() => setEscopoExclusao(null)}
        />
      )}
    </div>
  );
}

export default PlanejamentoModal;
